import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "card", "indicator"]
  static values = { 
    currentIndex: Number,
    autoScroll: Boolean,
    scrollInterval: Number
  }

  connect() {
    this.currentIndexValue = 0
    this.autoScrollValue = false
    this.scrollIntervalValue = 5000

    this.setupScrollListener()
    this.updateIndicators()
    
    // Auto-scroll functionality (optional)
    if (this.autoScrollValue) {
      this.startAutoScroll()
    }
  }

  disconnect() {
    this.stopAutoScroll()
  }

  setupScrollListener() {
    this.containerTarget.addEventListener('scroll', this.throttle(() => {
      this.updateCurrentIndex()
      this.updateIndicators()
    }, 100))
  }

  updateCurrentIndex() {
    const containerWidth = this.containerTarget.clientWidth
    const scrollLeft = this.containerTarget.scrollLeft
    const cardWidth = this.cardTargets[0]?.clientWidth || 320
    const gap = 16 // 1rem gap
    
    this.currentIndexValue = Math.round(scrollLeft / (cardWidth + gap))
  }

  updateIndicators() {
    this.indicatorTargets.forEach((indicator, index) => {
      if (index === this.currentIndexValue) {
        indicator.classList.add('bg-indigo-500')
        indicator.classList.remove('bg-gray-300', 'dark:bg-gray-600')
      } else {
        indicator.classList.remove('bg-indigo-500')
        indicator.classList.add('bg-gray-300', 'dark:bg-gray-600')
      }
    })
  }

  scrollToCard(event) {
    const index = parseInt(event.currentTarget.dataset.scrollIndicator)
    const cardWidth = this.cardTargets[0]?.clientWidth || 320
    const gap = 16 // 1rem gap
    
    this.containerTarget.scrollTo({
      left: index * (cardWidth + gap),
      behavior: 'smooth'
    })
  }

  next() {
    if (this.currentIndexValue < this.cardTargets.length - 1) {
      this.scrollToIndex(this.currentIndexValue + 1)
    } else {
      // Loop to first card
      this.scrollToIndex(0)
    }
  }

  previous() {
    if (this.currentIndexValue > 0) {
      this.scrollToIndex(this.currentIndexValue - 1)
    } else {
      // Loop to last card
      this.scrollToIndex(this.cardTargets.length - 1)
    }
  }

  scrollToIndex(index) {
    const cardWidth = this.cardTargets[0]?.clientWidth || 320
    const gap = 16 // 1rem gap
    
    this.containerTarget.scrollTo({
      left: index * (cardWidth + gap),
      behavior: 'smooth'
    })
  }

  startAutoScroll() {
    this.autoScrollTimer = setInterval(() => {
      this.next()
    }, this.scrollIntervalValue)
  }

  stopAutoScroll() {
    if (this.autoScrollTimer) {
      clearInterval(this.autoScrollTimer)
    }
  }

  pauseAutoScroll() {
    this.stopAutoScroll()
  }

  resumeAutoScroll() {
    if (this.autoScrollValue) {
      this.startAutoScroll()
    }
  }

  // Utility function to throttle scroll events
  throttle(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}
